import { API_ORIGIN, CONTENT_CDN_URL } from "../config/integrations";

const SCHEME_RE = /^[a-z][a-z\d+\-.]*:/i;
const STORAGE_OBJECT_SEGMENT = "/storage/v1/object/";
const STORAGE_ACCESS_MODES = new Set(["public", "sign", "authenticated"]);
const STORAGE_PROXY_PATH_RE = /\/(?:api\/v\d+\/)?storage\/object\/(.+)$/i;
const MEDIA_URL_FIELDS = [
  "android_player_url",
  "android_safe_url",
  "converted_url",
  "url",
  "thumbnail_url",
  "image_url",
  "media_url",
  "player_url",
  "media_player_url",
  "playback_url",
  "stream_url",
  "transcoded_url",
  "full_url",
  "content_url",
  "document_url",
  "pdf_url",
  "file_url",
];

const toTrimmedString = (value) => String(value ?? "").trim();
const pickFirst = (...values) => values.map(toTrimmedString).find(Boolean) || "";

const isAbsoluteUrl = (value) => {
  const raw = toTrimmedString(value);
  return Boolean(raw) && (SCHEME_RE.test(raw) || raw.startsWith("//"));
};

const r2ObjectKeyFromUrl = (value) => {
  const raw = toTrimmedString(value);
  if (!raw || !/^https?:\/\//i.test(raw)) return "";
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();
    const isR2Host =
      host.endsWith(".r2.dev") ||
      host.includes(".r2.cloudflarestorage.com") ||
      host.includes("r2.cloudflarestorage.com");
    if (!isR2Host) return "";
    const parts = parsed.pathname
      .replace(/^\/+/, "")
      .split("/")
      .filter(Boolean);
    if (!parts.length) return "";
    if (host.includes("r2.cloudflarestorage.com") && parts.length > 1) {
      parts.shift();
    }
    return decodeURIComponent(parts.join("/"));
  } catch {
    return "";
  }
};

const decodePath = (value) =>
  String(value || "")
    .split("/")
    .filter(Boolean)
    .map((part) => {
      try {
        return decodeURIComponent(part);
      } catch {
        return part;
      }
    })
    .join("/");

const storageProxyPathFromUrl = (value) => {
  const raw = toTrimmedString(value);
  if (!raw) return "";

  try {
    const parsed = new URL(raw, typeof window !== "undefined" ? window.location.origin : API_ORIGIN);
    const match = parsed.pathname.match(STORAGE_PROXY_PATH_RE);
    return match?.[1] ? decodePath(match[1]) : "";
  } catch {
    const match = raw.split(/[?#]/, 1)[0].match(STORAGE_PROXY_PATH_RE);
    return match?.[1] ? decodePath(match[1]) : "";
  }
};

const storageObjectPathFromUrl = (value) => {
  const raw = toTrimmedString(value);
  if (!raw || !raw.includes(STORAGE_OBJECT_SEGMENT)) {
    return "";
  }

  const remainder = raw
    .slice(raw.indexOf(STORAGE_OBJECT_SEGMENT) + STORAGE_OBJECT_SEGMENT.length)
    .split(/[?#]/, 1)[0]
    .replace(/^\/+/, "");
  const parts = decodePath(remainder).split("/").filter(Boolean);
  if (!parts.length) return "";

  let startIndex = 0;
  if (STORAGE_ACCESS_MODES.has(String(parts[0] || "").toLowerCase())) {
    startIndex = 1;
    if (parts.length > startIndex + 1) {
      startIndex += 1;
    }
  }

  return parts.slice(startIndex).join("/");
};

const toApiUrl = (value) => {
  const raw = toTrimmedString(value);
  if (!raw) return "";
  if (raw.startsWith("/")) return `${API_ORIGIN}${raw}`;
  return `${API_ORIGIN}/${raw}`;
};

const encodeStoragePath = (value) =>
  toTrimmedString(value)
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join("/");

const toStorageObjectUrl = (value) => {
  const storagePath = encodeStoragePath(value);
  if (!storagePath) return "";
  if (CONTENT_CDN_URL) return `${CONTENT_CDN_URL}/${storagePath}`;
  return `${API_ORIGIN}/api/v1/storage/object/${storagePath}`;
};

export const resolveMediaUrl = (value) => {
  const raw = toTrimmedString(value);
  if (!raw) return "";

  const storageProxyPath = storageProxyPathFromUrl(raw);
  if (storageProxyPath) return toStorageObjectUrl(storageProxyPath);
  const storageObjectPath = storageObjectPathFromUrl(raw);
  if (storageObjectPath) return toStorageObjectUrl(storageObjectPath);
  const r2ObjectKey = r2ObjectKeyFromUrl(raw);
  if (r2ObjectKey) return toStorageObjectUrl(r2ObjectKey);
  if (isAbsoluteUrl(raw)) return raw;
  if (raw.startsWith("/")) return toApiUrl(raw);

  return toStorageObjectUrl(raw) || toApiUrl(raw);
};

export const normalizeMediaField = (value) => {
  const raw = toTrimmedString(value);
  if (!raw) return raw;
  return resolveMediaUrl(raw) || raw;
};

export const normalizeMediaItem = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) {
    return item;
  }

  let changed = false;
  const nextItem = { ...item };
  MEDIA_URL_FIELDS.forEach((field) => {
    if (typeof nextItem[field] !== "string") return;
    const normalized = normalizeMediaField(nextItem[field]);
    if (normalized && normalized !== nextItem[field]) {
      nextItem[field] = normalized;
      changed = true;
    }
  });

  return changed ? nextItem : item;
};

export const normalizeMediaPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload.map((item) => normalizeMediaItem(item));
  }
  return normalizeMediaItem(payload);
};

export const getContentPreviewUrl = (item) => {
  if (!item) return "";
  return resolveMediaUrl(
    pickFirst(
      item.thumbnail_url,
      item.url,
      item.player_url,
      item.media_player_url,
      item.file_path,
      item.filePath,
      item.storage_path,
      item.media_url,
      item.full_url,
      item.content_url,
      item.document_url,
      item.pdf_url,
      item.path
    )
  );
};

export const getContentSourceUrl = (item) => {
  if (!item) return "";
  const type = String(item.type || item.media_type || item.mime_type || "").toLowerCase();
  const isVideo = type === "video" || type.startsWith("video/");
  return resolveMediaUrl(
    pickFirst(
      isVideo ? item.player_url : "",
      isVideo ? item.media_player_url : "",
      isVideo ? item.android_safe_url : "",
      isVideo ? item.playback_url : "",
      isVideo ? item.stream_url : "",
      item.url,
      item.player_url,
      item.media_player_url,
      item.file_path,
      item.filePath,
      item.storage_path,
      item.media_url,
      item.full_url,
      item.content_url,
      item.document_url,
      item.pdf_url,
      item.path
    )
  );
};

export const getContentOriginalVideoUrl = (item) => {
  if (!item) return "";
  return resolveMediaUrl(
    pickFirst(
      item.url,
      item.media_url,
      item.file_path,
      item.filePath,
      item.storage_path,
      item.full_url,
      item.content_url,
      item.path,
      item.player_url,
      item.media_player_url,
      item.android_safe_url,
      item.playback_url,
      item.stream_url,
      item.converted_url,
      item.transcoded_url
    )
  );
};
